/**
 * IndexedDB cache for large datasets that exceed localStorage quota
 * Can store hundreds of MB compared to localStorage's 5-10MB limit
 */

const DB_NAME = 'MarketplaceCache';
const DB_VERSION = 1;
const STORE_NAME = 'mapListings';

let dbPromise = null;

/**
 * Initialize IndexedDB connection
 */
const initDB = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('Not in browser'));
  
  if (dbPromise) return dbPromise;
  
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('❌ IndexedDB failed to open:', request.error);
      reject(request.error);
    };
    
    request.onsuccess = () => {
      console.log('✅ IndexedDB opened successfully');
      resolve(request.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
        console.log('✅ Created IndexedDB object store');
      }
    };
  });
  
  return dbPromise;
};

/**
 * Get cached data from IndexedDB
 * @param {string} key - Cache key
 * @returns {Promise<any|null>} Cached data or null if not found/expired
 */
export const getFromIndexedDB = async (key) => {
  try {
    const db = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);
      
      request.onsuccess = () => {
        const result = request.result;
        if (!result) {
          console.log(`❌ No IndexedDB cache for key: ${key}`);
          resolve(null);
        } else {
          console.log(`✅ Found IndexedDB cache for key: ${key}`, {
            listingCount: result.listingIds?.length,
            hasEntities: !!result.entities,
            age: Math.round((Date.now() - result.timestamp) / 1000 / 60) + 'min'
          });
          resolve(result);
        }
      };
      
      request.onerror = () => {
        console.error('❌ Error reading from IndexedDB:', request.error);
        reject(request.error);
      };
    });
  } catch (e) {
    console.error('❌ IndexedDB get error:', e);
    return null;
  }
};

/**
 * Save data to IndexedDB
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @returns {Promise<boolean>} Success status
 */
export const saveToIndexedDB = async (key, data) => {
  try {
    const db = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(data, key);
      
      request.onsuccess = () => {
        const sizeEstimate = JSON.stringify(data).length / 1024 / 1024;
        console.log(`✅ Saved to IndexedDB: ${key} (≈${sizeEstimate.toFixed(2)} MB)`);
        resolve(true);
      };
      
      request.onerror = () => {
        console.error('❌ Error writing to IndexedDB:', request.error);
        reject(request.error);
      };
      
      transaction.onerror = () => {
        console.error('❌ IndexedDB transaction error:', transaction.error);
        reject(transaction.error);
      };
    });
  } catch (e) {
    console.error('❌ IndexedDB save error:', e);
    return false;
  }
};

/**
 * Delete cached data from IndexedDB
 * @param {string} key - Cache key to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteFromIndexedDB = async (key) => {
  try {
    const db = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);
      
      request.onsuccess = () => {
        console.log(`✅ Deleted from IndexedDB: ${key}`);
        resolve(true);
      };
      
      request.onerror = () => {
        console.error('❌ Error deleting from IndexedDB:', request.error);
        reject(request.error);
      };
    });
  } catch (e) {
    console.error('❌ IndexedDB delete error:', e);
    return false;
  }
};

/**
 * Clear all cached data from IndexedDB
 * @returns {Promise<boolean>} Success status
 */
export const clearIndexedDB = async () => {
  try {
    const db = await initDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();
      
      request.onsuccess = () => {
        console.log('✅ Cleared all IndexedDB cache');
        resolve(true);
      };
      
      request.onerror = () => {
        console.error('❌ Error clearing IndexedDB:', request.error);
        reject(request.error);
      };
    });
  } catch (e) {
    console.error('❌ IndexedDB clear error:', e);
    return false;
  }
};
